import React, { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import type { Router as NgRouter } from '@angular/router';

interface IBackofficeWindow extends Window {
	ngRouter?: NgRouter;
}

/**
 * Navigates to an Angular backoffice module, then sets the React hash route.
 * Safe to call outside a react-router `<Router>`.
 */
export const navigateToBackofficeModule = (module: string, reactPath: string): void => {
	const backofficeWindow = window as IBackofficeWindow;
	const router = backofficeWindow.ngRouter;
	if (!router) {
		console.warn('navigateToBackofficeModule: window.ngRouter is not mounted');
		return;
	}

	const normalizedPath = reactPath.startsWith('/') ? reactPath.slice(1) : reactPath;
	void router.navigate([`/client/${module}`]).then(() => {
		if (normalizedPath !== '') {
			window.location.hash = `#/${normalizedPath}`;
		}
	});
};

export const useNavigation = () => {
	const history = useHistory();
	const location = useLocation();

	const ngNavigate = (module: string, reactPath: string) => {
		navigateToBackofficeModule(module, reactPath);
	};

	/**
	 * To navigate within the same system module, using the history.push() method by 'react-router'
	 * @param path a string of the wanted pathto push
	 */
	const pushNavigation = (path: string) => history.push(path);

	const getReturnPath = useCallback(
		(loc = location): string | null => {
			if (loc.state?.from) {
				return loc.state.from as string;
			}

			const returnUrl = new URLSearchParams(loc.search).get('return');
			if (returnUrl) {
				return returnUrl;
			}

			return null;
		},
		[location]
	);

	const goBack = useCallback(() => {
		const returnPath = getReturnPath(location);

		if (returnPath) {
			history.push(returnPath);
		} else {
			history.goBack();
		}
	}, [history, location]);

	return { ngNavigate, pushNavigation, goBack };
};
